/**
 * Ranking for the repo pickers: which of the caller's accessible repos should
 * be at the top of the list.
 *
 * The list arrives in whatever order the source produced (GitHub's `pushed`
 * order for the user scope, installation order for the App scope), which is
 * effectively arbitrary from the picker's point of view — an account with a
 * few hundred repos shows the user a wall of rows that has nothing to do with
 * the handful they actually work in. This module scores each row instead, from
 * how the person has used the picker before, and sorts by that score.
 *
 * Four independent signals, each normalized to roughly `[0, 1]` and then
 * weighted, so no single one can run away with the ordering:
 *
 *  - **Recency** — how long ago this repo was last picked. Decays by halves
 *    ({@link RECENCY_HALF_LIFE_MS}), so "the repo I was in this morning" beats
 *    "the repo I was in last month" without ever fully erasing the latter.
 *  - **Frequency** — the *rate* of recent picks: a per-selection decayed sum
 *    that saturates, so a repo picked ten times this week outranks one picked
 *    twice, while a burst of fifty picks cannot pin a repo to the top forever.
 *  - **Popularity** — the repo's all-time pick count, undecayed and log-scaled
 *    against the single busiest repo in the history
 *    (`log1p(count) / log1p(maxCount)`) — a ratio against that repo, not a
 *    share of all picks. This is the long memory: a workhorse repo keeps a
 *    good rank through a week spent elsewhere, which is exactly what recency
 *    and frequency alone get wrong.
 *  - **Freshness** — a cold-start prior from when the repo was last *updated*,
 *    not last pushed to. It reads `updatedAt`, GitHub's `updated_at`, which is
 *    repo *activity*: it moves on metadata-only changes (a description edit, a
 *    star, a rename) as well as on commits. So it is a proxy, and is weighted
 *    accordingly: it is the only signal a brand-new user has, and one real
 *    pick outweighs it.
 *
 * When the user is typing, **match quality** comes first as a hard tier rather
 * than as another weighted term: an exact hit outranks a prefix hit outranks a
 * bare substring hit, and the four signals above only order rows *within* a
 * tier. Making it a tier rather than a weight is deliberate — as a weight, a
 * daily-driver repo could outscore the repo whose name the user had just typed
 * in full, and no weighting that avoids that also leaves history any influence.
 * Filtering still belongs to `repo-search.ts`; this module only orders rows
 * that already matched.
 *
 * Everything here is pure and clock-injected so the weights can be tested
 * directly; the browser store that feeds it lives in `web/useRepoSelectionHistory.ts`.
 *
 * @module @reflex/plugin-github/shared/repo-ranking
 */

import { normalizeRepoQuery } from './repo-search.js';

const DAY_MS = 24 * 60 * 60 * 1000;

/** A repo picked today scores twice one picked a week ago. */
export const RECENCY_HALF_LIFE_MS = 7 * DAY_MS;

/** Individual picks fade slower than the last-pick timestamp does, so
 * "frequency" describes a habit over a couple of weeks rather than a day. */
export const FREQUENCY_HALF_LIFE_MS = 14 * DAY_MS;

/** Update recency stops distinguishing repos after a month or so of neglect. */
export const FRESHNESS_HALF_LIFE_MS = 30 * DAY_MS;

/**
 * Decayed-pick count at which the frequency term is ~63% saturated. Low
 * because picking a repo three times in a fortnight already says "this is one
 * of mine"; the difference between 20 and 40 picks says nothing more.
 */
export const FREQUENCY_SATURATION = 3;

/**
 * Signal weights. Their relative sizes are the actual product decision:
 * recency leads (the next pick is usually the last pick), frequency is a close
 * second, popularity is the tiebreaker with the long memory, and freshness is
 * a faint prior that only decides the order of repos with no history at all.
 */
export const RANK_WEIGHTS = {
  recency: 3,
  frequency: 2,
  popularity: 1.5,
  freshness: 0.5,
} as const;

/**
 * How directly a row answers what the user typed. Higher tiers sort ahead of
 * lower ones outright — {@link scoreRepo} only orders rows sharing a tier.
 */
export const MATCH_TIER = {
  /** The query is the repo's name or full slug. */
  exact: 3,
  /** The query starts the repo's name (`api` → `acme/api-gateway`). */
  namePrefix: 2,
  /** The query starts the full slug (`acme/api` → `acme/api-gateway`). */
  slugPrefix: 1,
  /** Matched somewhere else, or there is no query at all. */
  other: 0,
} as const;

/** What {@link rankRepos} needs off a repo row — satisfied by the web's
 * `GitHubRepo` and the server's `RepoResult` alike. */
export interface RepoRankable {
  fullName: string;
  name: string;
  /** ISO timestamp of the repo's last activity (GitHub's `updated_at`), when
   * the source knows it. */
  updatedAt?: string | null;
}

/** One repo's pick history, as persisted by the browser store. */
export interface RepoSelectionStats {
  /** All-time number of picks. Never decayed — this is the popularity term. */
  count: number;
  /** Epoch ms of the most recent pick. */
  lastSelectedAt: number;
  /** Epoch ms of the most recent picks, newest first and capped by the store.
   * Feeds the frequency term. */
  recentSelections: number[];
}

/** Pick history keyed by `owner/name`. */
export type RepoSelectionHistory = Readonly<Record<string, RepoSelectionStats>>;

export interface RankReposOptions {
  /** The user's pick history. Absent/empty means everything scores on
   * freshness and match quality alone. */
  history?: RepoSelectionHistory;
  /** The raw search box value, if any. Normalized internally. */
  query?: string;
  /** Injected clock, epoch ms. Defaults to `Date.now()`. */
  now?: number;
}

/** Fraction of a signal surviving `ageMs`, halving every `halfLifeMs`. Future
 * timestamps (clock skew) are treated as "now" rather than scoring above 1. */
function decay(ageMs: number, halfLifeMs: number): number {
  if (!Number.isFinite(ageMs) || ageMs <= 0) return 1;
  return 2 ** (-ageMs / halfLifeMs);
}

/** Which {@link MATCH_TIER} `repo` lands in for `query`. `query` must already
 * be normalized by `normalizeRepoQuery`. */
export function matchTier(repo: RepoRankable, query: string): number {
  if (!query) return MATCH_TIER.other;
  const fullName = repo.fullName.toLowerCase();
  const name = repo.name.toLowerCase();
  if (fullName === query || name === query) return MATCH_TIER.exact;
  if (name.startsWith(query)) return MATCH_TIER.namePrefix;
  if (fullName.startsWith(query)) return MATCH_TIER.slugPrefix;
  return MATCH_TIER.other;
}

/** The freshness prior: how recently the repo itself was updated (any
 * activity, not only a push). */
function freshness(repo: RepoRankable, now: number): number {
  if (!repo.updatedAt) return 0;
  const updatedAt = Date.parse(repo.updatedAt);
  if (Number.isNaN(updatedAt)) return 0;
  return decay(now - updatedAt, FRESHNESS_HALF_LIFE_MS);
}

/** The decayed pick rate, saturating so a burst cannot dominate forever. */
function frequency(stats: RepoSelectionStats, now: number): number {
  const decayed = stats.recentSelections.reduce(
    (sum, at) => sum + decay(now - at, FREQUENCY_HALF_LIFE_MS),
    0,
  );
  return 1 - Math.exp(-decayed / FREQUENCY_SATURATION);
}

/**
 * The within-tier score {@link rankRepos} sorts by, descending. Exported so
 * tests (and a debugging session) can assert on one repo instead of an
 * ordering.
 *
 * `maxCount` is the highest all-time pick count in the history, which is what
 * the popularity term is measured against — pass `0` when there is no history.
 */
export function scoreRepo(
  repo: RepoRankable,
  opts: { stats?: RepoSelectionStats; maxCount: number; now: number },
): number {
  const { stats, maxCount, now } = opts;
  const w = RANK_WEIGHTS;
  let score = w.freshness * freshness(repo, now);
  if (!stats || stats.count <= 0) return score;

  score += w.recency * decay(now - stats.lastSelectedAt, RECENCY_HALF_LIFE_MS);
  score += w.frequency * frequency(stats, now);
  // Log-scaled against the busiest repo so the gap between 1 and 5 picks
  // matters far more than the gap between 50 and 55.
  if (maxCount > 0) {
    score += w.popularity * (Math.log1p(stats.count) / Math.log1p(maxCount));
  }
  return score;
}

/**
 * Order `repos` best-first: by match tier, then by score within the tier. Pure —
 * returns a new array and never mutates the input. Ties break on `fullName` so
 * the order is fully determined by the inputs; a picker that reshuffled
 * equal-scoring rows between renders would be its own usability bug.
 */
export function rankRepos<T extends RepoRankable>(
  repos: readonly T[],
  opts: RankReposOptions = {},
): T[] {
  const { history, now = Date.now() } = opts;
  const query = normalizeRepoQuery(opts.query);
  const entries = history ? Object.values(history) : [];
  const maxCount = entries.reduce((max, stats) => Math.max(max, stats.count), 0);

  return repos
    .map((repo) => ({
      repo,
      tier: matchTier(repo, query),
      score: scoreRepo(repo, { stats: history?.[repo.fullName], maxCount, now }),
    }))
    .sort(
      (a, b) =>
        b.tier - a.tier || b.score - a.score || a.repo.fullName.localeCompare(b.repo.fullName),
    )
    .map((scored) => scored.repo);
}
